import { CheckBox } from "@bokio/designsystem/components/CheckBox/CheckBox";
import { Notice } from "@bokio/designsystem/components/Notice/Notice";
import { Paragraph } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { Link } from "@bokio/elements/Link/Link";
import Markdown from "@bokio/elements/Markdown/Markdown";
import { Th } from "@bokio/elements/Table";
import { AnalyticsEventCategory, useMetric } from "@bokio/hooks/useMetric/useMetric";
import { GeneralLangFactory } from "@bokio/lang";
import BankLangFactory from "@bokio/lang/BankLangFactory";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import { useActiveBankConnection } from "@bokio/mobile-web-shared/hooks/useActiveBankConnection/useActiveBankConnection";
import { SveaKycBlockServicesNotice } from "@bokio/settings/src/scenes/SveaKyc/SveaKycBlockServicesNotice";
import { getRoute } from "@bokio/shared/route";
import { formatNumberCurrency } from "@bokio/shared/utils/format";
import { mergeClassNames } from "@bokio/utils/classes";

import { SignAndPayRow } from "../../SignAndPayRow/SignAndPayRow";

import type * as m from "@bokio/mobile-web-shared/core/model/model";
import type { ReactNode } from "react";

import * as styles from "./signAndPayContent.scss";
import * as contentStyles from "./signAndPayContentGrid.scss";

type PaymentValidationRulesDto = m.Bokio.Bank.Contract.Dtos.PaymentValidationRulesDto;
type PaymentGroupForListDto = m.Bokio.Bank.Contract.Dtos.PaymentGroupForListDto;
type FromAccountInfo = m.Bokio.Bank.Contract.Dtos.FromAccountInfoDto;
type FailedPaymentGroup = m.Bokio.Bank.Contract.Dtos.FailedPaymentGroup;
interface SignAndPayContentProps {
	selectedPaymentGroupIds: string[];
	isPolling: boolean;
	availablePaymentGroups: PaymentGroupForListDto[];
	permittedPaymentGroupIds?: Set<string>;
	fromAccountsInfo: { [key: string]: FromAccountInfo };
	disabled: boolean;
	failedPaymentGroups: Record<string, FailedPaymentGroup>;
	error: string | undefined;
	onGlobalCheckClick: (checked: boolean) => void;
	togglePaymentGroupSelection: (id: string, checked: boolean) => void;
	onClose: () => void;
	paymentValidationRules: PaymentValidationRulesDto;
	hidePaymentList?: boolean;
	children: ReactNode;
}

export const SignAndPayContent = ({
	isPolling,
	selectedPaymentGroupIds,
	availablePaymentGroups,
	permittedPaymentGroupIds,
	fromAccountsInfo,
	disabled,
	failedPaymentGroups,
	error,
	onGlobalCheckClick,
	togglePaymentGroupSelection,
	onClose,
	paymentValidationRules,
	hidePaymentList,
	children,
}: SignAndPayContentProps) => {
	const generalLang = GeneralLangFactory();
	const bankLang = BankLangFactory();
	const [addMetric] = useMetric();
	const { companyInfo } = useCompanyInfo();
	const { shouldBlockBankServices } = useActiveBankConnection();

	const renderFromAccountInfo = (acc: FromAccountInfo) => {
		const accNumberAndName = `${bankLang.SignandPayModel_FromGroup} ${acc.AccountNumber} - ${acc.AccountName}`;

		return acc.Balance == null
			? accNumberAndName
			: accNumberAndName + " (" + formatNumberCurrency(acc.Balance, acc.CurrencyCode) + ")";
	};

	const renderPaymentGroupsByFromAccount = () => {
		const permittedGroups = permittedPaymentGroupIds
			? availablePaymentGroups.filter(pg => permittedPaymentGroupIds.has(pg.Id))
			: availablePaymentGroups;

		return (
			<table className={contentStyles.signAndPayContentGrid}>
				<thead>
					<tr>
						<Th className={mergeClassNames(contentStyles.itemGroupExpand)} />
						<Th className={mergeClassNames(contentStyles.itemSelect)}>
							<CheckBox
								className={mergeClassNames(styles.margin0)}
								checked={selectedPaymentGroupIds.length !== 0}
								partial={selectedPaymentGroupIds.length !== permittedGroups.length}
								onChange={onGlobalCheckClick}
							/>
						</Th>
						<Th className={mergeClassNames(contentStyles.itemDescription)}>{bankLang.PaymentTo}</Th>
						<Th className={mergeClassNames(contentStyles.itemDate)}>{generalLang.Date}</Th>
						<Th className={mergeClassNames(contentStyles.itemWarnings)} />
						<Th className={mergeClassNames(contentStyles.itemSum)}>{generalLang.Sum}</Th>
					</tr>
				</thead>
				<tbody>
					{Object.entries(fromAccountsInfo).map(kps => {
						const [accountId, acc] = kps;
						const paymentGroupsForAccount = availablePaymentGroups.filter(pg => pg.FromBankAccount === accountId);
						return (
							<tr key={accountId}>
								<table>
									<thead>
										<tr>{<th>{renderFromAccountInfo(acc)}</th>}</tr>
									</thead>
									{/* {thead(false)} */}
									<tbody>
										{paymentGroupsForAccount.map(paymentGroup => (
											<SignAndPayRow
												key={paymentGroup.Id}
												paymentGroup={paymentGroup}
												disabled={disabled}
												selected={selectedPaymentGroupIds.includes(paymentGroup.Id)}
												isPermitted={permittedPaymentGroupIds ? permittedPaymentGroupIds.has(paymentGroup.Id) : true}
												toggle={togglePaymentGroupSelection}
												failedPayments={failedPaymentGroups[paymentGroup.Id]?.FailedPayments ?? {}}
												paymentValidationRules={paymentValidationRules}
											/>
										))}
									</tbody>
								</table>
							</tr>
						);
					})}
				</tbody>
			</table>
		);
	};

	const renderNoPaymentGroups = (
		<div className={styles.emptyView}>
			<Paragraph style="heading-default">{generalLang.BBA_NoPaymentsOrTransfers}</Paragraph>
			<Paragraph marginAway="4">{generalLang.BBA_AddPaymentsOrTransfers}</Paragraph>
			<Paragraph marginAway="8">
				<Link
					route={getRoute("bankCreatePayment", { company: companyInfo.Id })}
					onClick={() => {
						onClose();
						addMetric(AnalyticsEventCategory.Bba, "GoToCreatePayment", "FromSignAndPayPopoverEmptyView");
					}}
				>
					{generalLang.BBA_CreatePaymentOrTransfer}
				</Link>
			</Paragraph>
		</div>
	);

	return (
		<>
			{!isPolling && !hidePaymentList && (
				<>
					{availablePaymentGroups.length > 0 ? renderPaymentGroupsByFromAccount() : renderNoPaymentGroups}
					{error && (
						<Notice color="error" margin title={bankLang.SignAndPayModalErrorTitle} collapsible={false}>
							<Markdown markdownContent={error} />
						</Notice>
					)}
					{shouldBlockBankServices && <SveaKycBlockServicesNotice margin={["top", "left", "right", "bottom"]} />}
				</>
			)}

			{children}
		</>
	);
};
